import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, Subject, interval, merge, of, forkJoin } from 'rxjs';
import { switchMap, filter, distinctUntilChanged, takeUntil, tap, catchError, map } from 'rxjs/operators';
import { APP_CONFIG } from '@environments/environment';
import { InProcessDocument, DocumentStatus } from '@/app/shared/types';
import { ResponseSingleWrapper } from '@/app/shared/dto';

export type EntityType = 'CIR' | 'MPD';

@Injectable({
    providedIn: 'root',
})
export class MLJobStatusService {
    private http = inject(HttpClient);
    private readonly apiUrl = APP_CONFIG.apiUrl;
    private readonly POLL_INTERVAL = 10000; // 10 seconds

    private stopPolling$ = new Subject<void>();
    private documentStatusChanged$ = new Subject<{ documentId: string; status: DocumentStatus }>();

    /**
     * Observable that emits when document status changes
     */
    get onDocumentStatusChanged$(): Observable<{ documentId: string; status: DocumentStatus }> {
        return this.documentStatusChanged$.asObservable();
    }

    startPollingMany(loaders: Array<() => Observable<InProcessDocument[]>>): Observable<InProcessDocument[]> {
        this.stopPolling();

        return merge(of(null), interval(this.POLL_INTERVAL)).pipe(
            switchMap(() => {
                return loaders.length ? forkJoin(loaders.map((fn) => fn())) : of([] as InProcessDocument[][]);
            }),
            map((docLists: InProcessDocument[][]) => {
                const all = docLists.flat();
                const byId = new Map<string, InProcessDocument>();
                for (const d of all) {
                    byId.set(d.documentId, d);
                }
                return Array.from(byId.values());
            }),
            catchError((error) => {
                console.error('Error polling for in-process documents:', error);
                return of([] as InProcessDocument[]);
            }),
            distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)),
            takeUntil(this.stopPolling$),
        );
    }

    /**
     * Get all documents currently being processed by ML pipeline
     */
    getInProcessDocuments(entityType: EntityType): Observable<InProcessDocument[]> {
        const endpoint =
            entityType === 'CIR'
                ? `${this.apiUrl}/client-information-records/documents/in-process`
                : `${this.apiUrl}/products/documents/in-process`;

        return this.http
            .get<ResponseSingleWrapper<InProcessDocument[]>>(endpoint, { withCredentials: true })
            .pipe(map((response) => response.data || []));
    }

    getApprovedCarrierInProcessDocuments(): Observable<InProcessDocument[]> {
        const endpoint = `${this.apiUrl}/products/approved-carriers/documents/in-process`;
        return this.http
            .get<ResponseSingleWrapper<InProcessDocument[]>>(endpoint, { withCredentials: true })
            .pipe(
                map((response) => response.data || []),
                map((docs) => docs.map((d) => ({ ...d, entityType: 'APPROVED_CARRIER' } as InProcessDocument))),
            );
    }

    getFinishedProductSpecificationInProcessDocuments(): Observable<InProcessDocument[]> {
        const endpoint = `${this.apiUrl}/products/finished-product-specifications/documents/in-process`;
        return this.http
            .get<ResponseSingleWrapper<InProcessDocument[]>>(endpoint, { withCredentials: true })
            .pipe(
                map((response) => response.data || []),
                map((docs) =>
                    docs.map((d) => ({ ...d, entityType: 'FINISHED_PRODUCT_SPECIFICATION' } as InProcessDocument)),
                ),
            );
    }

    getSampleRequestSummaryInProcessDocuments(): Observable<InProcessDocument[]> {
        const endpoint = `${this.apiUrl}/products/sample-request-summary/documents/in-process`;
        return this.http
            .get<ResponseSingleWrapper<InProcessDocument[]>>(endpoint, { withCredentials: true })
            .pipe(
                map((response) => response.data || []),
                map((docs) =>
                    docs.map((d) => ({ ...d, entityType: 'SAMPLE_REQUEST_SUMMARY' } as InProcessDocument)),
                ),
            );
    }

    /**
     * Manually sync ML job status for a document
     */
    syncDocumentStatus(documentId: string, entityType: EntityType): Observable<DocumentStatus> {
        const endpoint =
            entityType === 'CIR'
                ? `${this.apiUrl}/client-information-records/documents/${documentId}/sync-status`
                : `${this.apiUrl}/products/documents/${documentId}/sync-status`;

        return this.http
            .post<ResponseSingleWrapper<DocumentStatus>>(endpoint, {}, { withCredentials: true })
            .pipe(map((response) => response.data));
    }

    syncApprovedCarrierDocumentStatus(documentId: string): Observable<DocumentStatus> {
        const endpoint = `${this.apiUrl}/products/approved-carriers/documents/${documentId}/sync-status`;
        return this.http
            .post<ResponseSingleWrapper<DocumentStatus>>(endpoint, {}, { withCredentials: true })
            .pipe(map((response) => response.data));
    }

    syncFinishedProductSpecificationDocumentStatus(documentId: string): Observable<DocumentStatus> {
        const endpoint = `${this.apiUrl}/products/finished-product-specifications/documents/${documentId}/sync-status`;
        return this.http
            .post<ResponseSingleWrapper<DocumentStatus>>(endpoint, {}, { withCredentials: true })
            .pipe(map((response) => response.data));
    }

    /**
     * Cancel ML processing job for a document
     */
    cancelMLJob(documentId: string, entityType: EntityType): Observable<any> {
        const endpoint =
            entityType === 'CIR'
                ? `${this.apiUrl}/client-information-records/documents/${documentId}/cancel`
                : `${this.apiUrl}/products/documents/${documentId}/cancel`;

        return this.http
            .post<ResponseSingleWrapper<any>>(endpoint, {}, { withCredentials: true })
            .pipe(map((response) => response.data));
    }

    /**
     * Start polling for in-process documents
     * Returns an observable that emits the list of in-process documents every POLL_INTERVAL
     */
    startPolling(entityType: EntityType): Observable<InProcessDocument[]> {
        // Stop any existing polling
        this.stopPolling();

        // Create polling observable
        return merge(
            // Emit immediately on subscription
            of(null),
            // Then emit on interval
            interval(this.POLL_INTERVAL)
        ).pipe(
            switchMap(() => this.getInProcessDocuments(entityType)),
            catchError((error) => {
                console.error('Error polling for in-process documents:', error);
                return of([] as InProcessDocument[]);
            }),
            distinctUntilChanged((prev, curr) => {
                // Only emit if the list has changed
                return JSON.stringify(prev) === JSON.stringify(curr);
            }),
            takeUntil(this.stopPolling$)
        );
    }

    /**
     * Stop polling for in-process documents
     */
    stopPolling(): void {
        this.stopPolling$.next();
    }

    /**
     * Poll for a specific document status until it reaches a terminal state
     */
    pollDocumentStatus(
        documentId: string,
        entityType: EntityType,
        pollInterval = this.POLL_INTERVAL
    ): Observable<DocumentStatus> {
        return interval(pollInterval).pipe(
            switchMap(() => this.syncDocumentStatus(documentId, entityType)),
            tap((status) => {
                // Emit status change event
                this.documentStatusChanged$.next({ documentId, status });
            }),
            filter((status) => {
                // Stop polling when status reaches terminal state
                return ['COMPLETED', 'FAILED', 'REJECTED'].includes(status.mlJobStatus);
            }),
            catchError((error) => {
                console.error(`Error polling document status for ${documentId}:`, error);
                throw error;
            })
        );
    }

    /**
     * Get status badge class for ML job status
     */
    getStatusBadgeClass(status: string): string {
        switch (status) {
            case 'PENDING':
                return 'bg-blue-100 text-blue-800';
            case 'RUNNING':
                return 'bg-yellow-100 text-yellow-800';
            case 'COMPLETED':
                return 'bg-green-100 text-green-800';
            case 'FAILED':
            case 'REJECTED':
                return 'bg-red-100 text-red-800';
            default:
                return 'bg-gray-100 text-gray-800';
        }
    }

    /**
     * Get status icon class for ML job status
     */
    getStatusIcon(status: string): string {
        switch (status) {
            case 'PENDING':
                return 'pi pi-clock';
            case 'RUNNING':
                return 'pi pi-spin pi-spinner';
            case 'COMPLETED':
                return 'pi pi-check-circle';
            case 'FAILED':
            case 'REJECTED':
                return 'pi pi-times-circle';
            default:
                return 'pi pi-question-circle';
        }
    }

    /**
     * Get status label for display
     */
    getStatusLabel(status: string): string {
        switch (status) {
            case 'PENDING':
                return 'Pending';
            case 'RUNNING':
                return 'Processing';
            case 'COMPLETED':
                return 'Completed';
            case 'FAILED':
                return 'Failed';
            case 'REJECTED':
                return 'Rejected';
            default:
                return 'Unknown';
        }
    }

    /**
     * Check if status is in-process (not terminal)
     */
    isInProcess(status: string): boolean {
        return ['PENDING', 'RUNNING'].includes(status);
    }

    /**
     * Check if status is terminal
     */
    isTerminal(status: string): boolean {
        return ['COMPLETED', 'FAILED', 'REJECTED'].includes(status);
    }

    /**
     * Format processing duration for display
     */
    formatDuration(seconds: number): string {
        if (seconds < 60) {
            return `${seconds}s`;
        }
        const minutes = Math.floor(seconds / 60);
        const remainingSeconds = seconds % 60;
        return `${minutes}m ${remainingSeconds}s`;
    }

    /**
     * Format confidence for display
     */
    formatConfidence(confidence?: number): string {
        if (confidence === undefined || confidence === null) {
            return 'N/A';
        }
        return `${confidence.toFixed(1)}%`;
    }

    /**
     * Clean up resources
     */
    ngOnDestroy(): void {
        this.stopPolling();
        this.documentStatusChanged$.complete();
    }
}
